import { useReveal } from '@/hooks/useReveal';

const automations = [
  {
    icon: 'ri-user-add-line',
    title: 'Lead Capture Automation',
    description: 'Auto-capture leads from WhatsApp, Facebook Ads, Instagram, and Google Sheets. Instantly qualify and assign to the right salesperson.',
    workflows: ['Facebook Lead Ads → WhatsApp', 'Instagram DM → CRM', 'Google Sheets → Auto Reply'],
    color: 'from-primary-500/20 to-primary-400/10',
    iconBg: 'bg-primary-100',
    iconColor: 'text-primary-600',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Sales Automation',
    description: 'Automated follow-up sequences, drip campaigns, abandoned cart recovery, and cross-sell recommendations that close more deals.',
    workflows: ['Auto Follow-up Sequence', 'Cart Recovery Messages', 'Cross-sell Recommendations'],
    color: 'from-accent-500/20 to-accent-400/10',
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-600',
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Marketing Automation',
    description: 'Schedule promotional campaigns, festival offers, new arrivals, and seasonal sales. Segment audiences for hyper-targeted messaging.',
    workflows: ['Festival Campaign Scheduler', 'Audience Segmentation', 'A/B Test Messages'],
    color: 'from-secondary-500/20 to-secondary-400/10',
    iconBg: 'bg-secondary-100',
    iconColor: 'text-secondary-600',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Customer Support Automation',
    description: 'AI chatbot handles 80% of queries instantly. Complex issues get smartly routed to the right agent with full conversation context.',
    workflows: ['AI Chatbot 24/7', 'Smart Agent Routing', 'Auto Ticket Creation'],
    color: 'from-violet-500/20 to-violet-400/10',
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Appointment Automation',
    description: 'Auto-booking, smart reminders, rescheduling, and cancellation handling. Reduce no-shows by up to 70%.',
    workflows: ['Auto Booking Link', '3-Stage Reminders', 'Smart Rescheduling'],
    color: 'from-amber-500/20 to-amber-400/10',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  {
    icon: 'ri-bank-card-line',
    title: 'Payment & Invoice Automation',
    description: 'Send invoices, payment links, and payment reminders automatically. Collect payments directly on WhatsApp.',
    workflows: ['Auto Invoice Generation', 'Payment Link Sharing', 'Overdue Reminders'],
    color: 'from-emerald-500/20 to-emerald-400/10',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
  {
    icon: 'ri-star-smile-line',
    title: 'Review & Feedback Automation',
    description: 'Automatically request Google reviews and feedback after every sale or service. Build your online reputation effortlessly.',
    workflows: ['Post-Sale Review Request', 'Google Review Link', 'Sentiment Analysis'],
    color: 'from-rose-500/20 to-rose-400/10',
    iconBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
  },
  {
    icon: 'ri-notification-3-line',
    title: 'Internal Notifications',
    description: 'Auto-alert your team on Slack, email, or WhatsApp when a hot lead arrives, payment is received, or a deal is won.',
    workflows: ['Slack Notifications', 'Email Alerts', 'Team WhatsApp Groups'],
    color: 'from-cyan-500/20 to-cyan-400/10',
    iconBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
  },
];

export default function AutomationSection() {
  const { ref, visible } = useReveal();

  return (
    <section id="solutions" className="relative py-20 md:py-28 bg-background-100 overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-primary-500/3 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-accent-500/3 blur-3xl pointer-events-none"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold uppercase tracking-wider mb-5">
            Business Automation
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-5 leading-tight">
            Set It Up Once.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">
              It Works Forever.
            </span>
          </h2>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            Create powerful automation workflows that run 24/7 without human intervention.
            Every business process — automated, monitored, and optimized.
          </p>
        </div>

        {/* Automation Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {automations.map(automation => (
            <div
              key={automation.title}
              className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-7 hover:border-secondary-200/50 transition-all duration-400 hover:-translate-y-1"
            >
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-12 h-12 rounded-xl ${automation.iconBg} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-400`}>
                  <i className={`${automation.icon} text-xl ${automation.iconColor}`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold font-heading text-foreground-950 mb-1.5">{automation.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{automation.description}</p>
                </div>
              </div>

              {/* Workflow tags */}
              <div className="flex flex-wrap gap-2">
                {automation.workflows.map(wf => (
                  <span
                    key={wf}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-600"
                  >
                    <i className="ri-git-branch-line text-secondary-400 text-xs"></i>
                    {wf}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}